/**
 * Public sold-card archive (TNK-117 phase C). Buyer-facing trust + search-
 * continuity surface: lets browsers see what's actually moving, and gives
 * search-engine traffic for already-sold cards a landing page that
 * doesn't 404.
 *
 * V1 lists Card-direct sales only — Product bundle sales delete their
 * card rows on ship, so unfolding them would yield broken pointers. A
 * follow-up can render a "bundle summary" row for those.
 *
 * Sale prices are hidden by default. An operator opt-in could surface
 * them later via the store profile; v1 keeps things conservative since
 * sale-price disclosure is a competitive call and the trust signal
 * doesn't need it.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, ChevronLeft, Loader2 } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";
import { fetchStorefrontSold, type SoldCard } from "@/features/storefront";

export const Route = createFileRoute("/shop/sold")({
  component: ShopSoldPage,
});

const PAGE_SIZE = 48;

type LoadState =
  | { phase: "loading" }
  | {
      phase: "ready";
      cards: SoldCard[];
      total: number;
      loadingMore: boolean;
    }
  | { phase: "error"; message: string };

function ShopSoldPage() {
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const out = await fetchStorefrontSold({ limit: PAGE_SIZE });
        if (cancelled) return;
        setState({
          phase: "ready",
          cards: out.cards,
          total: out.total,
          loadingMore: false,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: "error",
          message: (err as Error).message ?? "Couldn't load sold archive.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = async () => {
    if (state.phase !== "ready" || state.loadingMore) return;
    if (state.cards.length >= state.total) return;
    setState({ ...state, loadingMore: true });
    try {
      const out = await fetchStorefrontSold({
        limit: PAGE_SIZE,
        offset: state.cards.length,
      });
      setState({
        phase: "ready",
        cards: [...state.cards, ...out.cards],
        total: out.total,
        loadingMore: false,
      });
    } catch {
      setState({ ...state, loadingMore: false });
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link
        to="/shop/inventory"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft size={12} />
        Back to catalog
      </Link>

      <div className="mt-3 flex items-baseline gap-3">
        <h1
          className="shop-display font-black"
          style={{
            fontSize: "clamp(20px, 3.5vw, 32px)",
            color: "var(--color-foreground)",
          }}
        >
          Recently Sold
        </h1>
        {state.phase === "ready" && state.total > 0 && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold"
            style={{
              background: "rgba(74,222,128,0.12)",
              borderColor: "rgba(74,222,128,0.35)",
              color: "#4ade80",
            }}
          >
            <CheckCircle2 size={11} />
            {state.total} shipped
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Single-card sales the store has shipped to buyers. Sold cards are
        unavailable for purchase — browse the{" "}
        <Link
          to="/shop/inventory"
          className="text-primary underline hover:opacity-90"
        >
          live catalog
        </Link>{" "}
        for what's still in stock.
      </p>

      {state.phase === "loading" && (
        <div className="py-16 text-center text-muted-foreground">
          <Loader2 size={20} className="mx-auto animate-spin" />
          <p className="mt-3 text-sm">Loading sold archive…</p>
        </div>
      )}

      {state.phase === "error" && (
        <p className="py-16 text-center text-sm text-destructive">
          {state.message}
        </p>
      )}

      {state.phase === "ready" && state.cards.length === 0 && (
        <div className="mt-8 rounded-lg border border-dashed border-border bg-muted/20 p-12 text-center">
          <p className="text-sm font-medium text-foreground">
            No shipped cards yet.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Sold single-card listings will appear here as they ship.
          </p>
        </div>
      )}

      {state.phase === "ready" && state.cards.length > 0 && (
        <>
          <div
            className="mt-6 grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            }}
          >
            {state.cards.map((card) => (
              <SoldCardTile key={`${card.id}-${card.soldAt}`} card={card} />
            ))}
          </div>
          {state.cards.length < state.total && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={state.loadingMore}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                {state.loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
          {state.cards.length >= state.total && (
            <p className="mt-6 text-center text-xs text-muted-foreground">
              End of archive
            </p>
          )}
        </>
      )}
    </div>
  );
}

function SoldCardTile({ card }: { card: SoldCard }) {
  const img = card.imageFrontRef ? imageUrl(card.imageFrontRef, "thumb") : null;
  const soldDate = card.soldAt
    ? new Date(card.soldAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <Link
      to="/shop/card/$id"
      params={{ id: card.id }}
      className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-md transition-shadow hover:shadow-lg"
    >
      <div
        className="relative w-full overflow-hidden bg-muted"
        style={{ aspectRatio: "2.5 / 3.5" }}
      >
        {img && (
          <img
            src={img}
            alt={card.name}
            className="h-full w-full object-cover"
            style={{ opacity: 0.6, filter: "grayscale(0.4)" }}
            loading="lazy"
          />
        )}
        <span
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: "rgba(74,222,128,0.18)",
            borderColor: "rgba(74,222,128,0.45)",
            color: "#4ade80",
          }}
        >
          Sold
        </span>
      </div>
      <div className="flex flex-col gap-0.5 px-3 py-2">
        <div className="truncate text-[13px] font-semibold text-foreground">
          {card.name}
        </div>
        <div className="truncate text-[10px] text-muted-foreground">
          {card.setName}
          {card.setCode ? ` · ${card.setCode}` : ""}
          {card.cardNumber ? ` · ${card.cardNumber}` : ""}
        </div>
        {soldDate && (
          <div className="mt-1 text-[10px] text-muted-foreground">
            Sold {soldDate}
          </div>
        )}
      </div>
    </Link>
  );
}
